// src/components/Layout.js
import React, { useState, useRef, useEffect, useMemo, useCallback, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Offcanvas } from "bootstrap";

// role-based nav config
import { NavItems, canSeeNavItem, RoleLabels } from "../utils/access";

// user + tenant context
import { useUser } from "../utils/userContext";
import { useTier } from "../utils/tierContext";
import useAuthToken from "../hooks/useAuthToken";
import SubscriptionBanner from "./SubscriptionBanner";
import styles from "./Layout.module.css";
import HelpDrawer from "./HelpDrawer";
import { getHelpTopicForPath } from "../utils/helpTopics";
import { clearHelpSession } from "../utils/helpSession";

const NotificationBell = React.lazy(() => import("./NotificationBell"));

const PageTitles: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/clients": "Clients",
  "/prospects": "Prospects",
  "/timeentry": "Time Entry",
  "/approvals": "Approvals",
  "/reports": "Reports",
  "/editprofile": "Edit Profile",
  "/selecttenant": "Select Tenant",
  "/settings": "Settings",
  "/settings/company": "Settings",
  "/settings/users": "Settings",
  "/settings/billing": "Settings",
  "/settings/features": "Settings",
  "/settings/jobtypes": "Settings",
  "/settings/expensetypes": "Settings",
  "/settings/billingroles": "Settings",
  "/settings/audit": "Settings",
  "/sandbox": "Time Entry",
  "/dev/tiers": "Dev Tools",
};

const PageSubtitles: Record<string, string> = {
  "/dashboard": "See workspace activity, time trends, and client performance at a glance.",
  "/sandbox": "Track daily work and submit time for the current billing period.",
  "/timeentry": "Track daily work and submit time for the current billing period.",
  "/prospects": "Manage prospects for the selected client and maintain related tracts and assignments.",
  "/clients": "Maintain client details, billing rates, and fee structure.",
  "/editprofile": "Maintain your profile information used across the workspace.",
  "/approvals": "Review and approve or reject pending time entries assigned to you.",
  "/reports": "Generate, preview, and export reports for your workspace.",
  "/selecttenant": "Choose which company you want to work in.",
  "/dev/tiers": "Preview how the app looks across subscription tiers.",
};

const resolvePageTitle = (pathname: string) => {
  if (!pathname) return "";

  const exact = PageTitles[pathname];
  if (exact) return exact;

  const bestPrefix = Object.keys(PageTitles)
    .filter((key) => pathname === key || pathname.startsWith(`${key}/`))
    .sort((a, b) => b.length - a.length)[0];

  return bestPrefix ? PageTitles[bestPrefix] : "";
};

const getDefaultNavRoute = (roleId: any, hasFeature?: (key: string) => boolean) => {
  const visibleItems = NavItems.filter((item) => canSeeNavItem(roleId, item, hasFeature));
  return visibleItems[0]?.to || "/timeentry";
};

// ----------------------------
// Initials Avatar
// ----------------------------
const hashToHsl = (input: any) => {
  const str = String(input || "");
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 52%)`;
};

const getInitials = ({ firstName, lastName, fullName, email }: { firstName?: string; lastName?: string; fullName?: string; email?: string } = {}) => {
  const f = (firstName || "").trim();
  const l = (lastName || "").trim();

  if (f || l) return `${f[0] || ""}${l[0] || ""}`.toUpperCase();

  const fn = (fullName || "").trim();
  if (fn) {
    const parts = fn.split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] || "";
    const b = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";
    return `${a}${b}`.toUpperCase() || "?";
  }

  const em = (email || "").trim();
  if (em) return em[0].toUpperCase();

  return "?";
};

const InitialsAvatar = ({
  firstName,
  lastName,
  fullName,
  email,
  size = 36,
  className = "",
  title = "",
}: {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  size?: number;
  className?: string;
  title?: string;
}) => {
  const initials = useMemo(
    () => getInitials({ firstName, lastName, fullName, email }),
    [firstName, lastName, fullName, email],
  );

  const bg = useMemo(
    () => hashToHsl(email || fullName || `${firstName}${lastName}` || initials),
    [email, fullName, firstName, lastName, initials],
  );

  return (
    <div
      className={`${className} ${styles.initialsAvatar}`}
      title={title}
      aria-label={title || "User profile"}
      style={{
        width: size,
        height: size,
        background: bg,
      }}
    >
      <span style={{ fontSize: Math.max(12, Math.round(size * 0.38)) }}>
        {initials}
      </span>
    </div>
  );
};

// ----------------------------
// Small inline icons
// ----------------------------
const BuildingIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M4.167 16.667V5.833A1.667 1.667 0 0 1 5.833 4.167h5v12.5M10.833 16.667h5V9.167a1.667 1.667 0 0 0-1.666-1.667h-3.334"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.667 7.5h1.666M6.667 10h1.666M12.5 10h1.667M12.5 12.5h1.667"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M10 10a3.333 3.333 0 1 0 0-6.667A3.333 3.333 0 0 0 10 10Z"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M4.167 16.667a5.833 5.833 0 0 1 11.666 0"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M7.5 15.833H5.833A1.667 1.667 0 0 1 4.167 14.167V5.833A1.667 1.667 0 0 1 5.833 4.167H7.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.667 13.333 15 10l-3.333-3.333M15 10H7.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HelpIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M10 17.5a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15Z"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M7.817 7.75A2.25 2.25 0 0 1 10 6.25c1.25 0 2.167.783 2.167 1.933 0 .85-.467 1.417-1.25 1.884-.667.4-.917.716-.917 1.433"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M10 14.167h.008"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2"
    />
  </svg>
);

// ----------------------------
// Shared profile action row
// ----------------------------
const ProfileActionRow = ({
  to,
  onClick,
  isActive = false,
  icon,
  title,
  subtitle,
  danger = false,
  asButton = false,
}: {
  to?: any;
  onClick?: any;
  isActive?: boolean;
  icon: any;
  title: any;
  subtitle: any;
  danger?: boolean;
  asButton?: boolean;
}) => {
  const content = (
    <>
      <span
        className={`icon ${styles.profileActionIcon}`}
        style={danger ? { color: "#B42318" } : undefined}
      >
        {icon}
      </span>
      <div>
        <div className="title" style={danger ? { color: "#B42318" } : undefined}>
          {title}
        </div>
        <div className="subtitle">{subtitle}</div>
      </div>
    </>
  );

  if (asButton) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`profile-item ${styles.profileActionButton}`}
      >
        {content}
      </button>
    );
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`profile-item ${isActive ? "active" : ""}`}
    >
      {content}
    </Link>
  );
};

// ----------------------------
// Header Component
// ----------------------------
const Header = () => {
  const [isProfileVisible, setProfileVisible] = useState(false);
  const [isHelpOpen, setHelpOpen] = useState(false);
  const location = useLocation();
  const { instance } = useMsal();
  const { user, tenants, activeTenant, activeTenantId } = useUser();
  const { token, getToken } = useAuthToken();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const stableGetToken = useCallback(() => getTokenRef.current(), []);

  const dropdownRef = useRef<any>(null);
  const profileButtonRef = useRef<HTMLButtonElement | null>(null);

  const toggleProfile = () => setProfileVisible((v) => !v);

  useEffect(() => {
    setProfileVisible(false);
    setHelpOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isProfileVisible) return;

    const onMouseDown = (e: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) {
        setProfileVisible(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setProfileVisible(false);
        profileButtonRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isProfileVisible]);

  const pageName = resolvePageTitle(location.pathname);
  const pageSubtitle = PageSubtitles[location.pathname] || "";
  const helpTopic = getHelpTopicForPath(location.pathname);

  const roleId = Number(user?.roleId);
  const roleLabel = RoleLabels[roleId] || "Member";
  const canSwitchTenants = (tenants?.length ?? 0) > 1;

  const activeTenantName: string =
    activeTenant?.tenantName ??
    (activeTenant as any)?.TenantName ??
    (activeTenant as any)?.businessName ??
    (activeTenant as any)?.BusinessName ??
    "Tenant";

  const displayName: string =
    (user as any)?.fullName ||
    (user?.firstName || user?.lastName
      ? `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim()
      : "User");

  const handleSignOut = async () => {
    setProfileVisible(false);
    await clearHelpSession();
    instance.logoutRedirect({ postLogoutRedirectUri: "/" });
  };

  return (
    <div className="inr-side w-d-0 wdt-md-0 header-sticky">
      <div className="d-flex right-side-head">
        {pageSubtitle ? (
          <div style={{ paddingTop: 6, paddingLeft: 12 }}>
            <h2>{pageName}</h2>
            <p className="header-subtitle">{pageSubtitle}</p>
          </div>
        ) : (
          <h2>{pageName}</h2>
        )}

        <div className="ms-auto bell-btn" ref={dropdownRef}>
          <ul>
            <li>
              <button
                type="button"
                className={`${styles["layout-help-button"]} ${isHelpOpen ? styles["layout-help-button--active"] : ""}`}
                onClick={() => {
                  setProfileVisible(false);
                  setHelpOpen(true);
                }}
                aria-label="Open help"
                aria-haspopup="dialog"
                aria-expanded={isHelpOpen}
                data-tooltip={helpTopic ? `Help: ${helpTopic.title}` : "Help"}
              >
                <HelpIcon />
              </button>
            </li>
            <li>
              <Suspense fallback={null}>
                <NotificationBell token={token} getToken={stableGetToken} userId={user?.userId ?? null} activeTenantId={activeTenantId ?? null} />
              </Suspense>
            </li>
            <li className={styles.profileListItem}>
              <button
                ref={profileButtonRef}
                type="button"
                className={`profile-avatar ${styles.profileAvatarButton}`}
                onClick={toggleProfile}
                aria-label="Open profile menu"
                aria-haspopup="menu"
                aria-expanded={isProfileVisible}
              >
                <InitialsAvatar
                  size={36}
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                  fullName={displayName}
                  email={user?.emailAddress || (user as any)?.EmailAddress}
                  title={displayName}
                />
              </button>

              {isProfileVisible && (
                <div className="profile-dropdown" role="menu">
                  <div className={styles.tenantCard}>
                    <div className={styles.tenantCardBody}>
                      <div className={styles.tenantLabel}>
                        Active tenant
                      </div>
                      <div
                        className={styles.tenantName}
                        title={activeTenantName}
                      >
                        {activeTenantName}
                      </div>
                    </div>

                    <span className={styles.roleBadge}>
                      {roleLabel}
                    </span>
                  </div>

                  {canSwitchTenants && (
                    <ProfileActionRow
                      to="/selecttenant"
                      onClick={() => setProfileVisible(false)}
                      isActive={location.pathname === "/selecttenant"}
                      icon={<BuildingIcon />}
                      title="Switch Tenants"
                      subtitle="Change your active company"
                    />
                  )}

                  <ProfileActionRow
                    to="/editprofile"
                    onClick={() => setProfileVisible(false)}
                    isActive={location.pathname === "/editprofile"}
                    icon={<UserIcon />}
                    title="Edit Profile"
                    subtitle="Manage your personal details"
                  />

                  <div className={styles.profileDivider} />

                  <ProfileActionRow
                    asButton
                    onClick={handleSignOut}
                    icon={<LogoutIcon />}
                    title="Sign Out"
                    subtitle="End your session"
                    danger
                  />
                </div>
              )}
            </li>
          </ul>
        </div>
      </div>
      <HelpDrawer
        isOpen={isHelpOpen}
        onClose={() => setHelpOpen(false)}
        topic={helpTopic}
      />
    </div>
  );
};

// ----------------------------
// Mobile OffCanvas Menu
// ----------------------------
const MobileNavBar = () => {
  const location = useLocation();
  const { instance } = useMsal();
  const [isHelpOpen, setHelpOpen] = useState(false);

  const { user, tenants, activeTenant, isApprover } = useUser();
  const { hasFeature } = useTier();
  const roleId = Number(user?.roleId);
  const canSwitchTenants = (tenants?.length ?? 0) > 1;
  const homeRoute = getDefaultNavRoute(roleId, hasFeature);

  const roleLabel = RoleLabels[roleId] || "Member";

  const activeTenantName: string =
    activeTenant?.tenantName ??
    (activeTenant as any)?.TenantName ??
    (activeTenant as any)?.businessName ??
    (activeTenant as any)?.BusinessName ??
    "Tenant";

  const displayName: string =
    (user as any)?.fullName ||
    (user?.firstName || user?.lastName
      ? `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim()
      : "User");

  useEffect(() => {
    const el = document.getElementById("sidebarNav");
    if (!el) return;

    const API =
      typeof Offcanvas !== "undefined"
        ? Offcanvas
        : (window as any).bootstrap?.Offcanvas;
    if (!API) return;

    API.getOrCreateInstance(el);

    const hardCleanup = () => {
      document.querySelectorAll(".offcanvas-backdrop").forEach((b) => b.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.style.touchAction = "";
      document.documentElement.style.overflow = "";
    };

    const onHidden = () => hardCleanup();

    el.addEventListener("hidden.bs.offcanvas", onHidden);

    return () => {
      el.removeEventListener("hidden.bs.offcanvas", onHidden);
    };
  }, []);

  const openMenu = () => {
    const el = document.getElementById("sidebarNav");
    if (!el) return;

    const API =
      typeof Offcanvas !== "undefined"
        ? Offcanvas
        : (window as any).bootstrap?.Offcanvas;
    if (!API) return;

    const instance = API.getOrCreateInstance(el);
    if (!el.classList.contains("show") && !el.classList.contains("showing")) {
      instance.show();
    }
  };

  const closeMenu = () => {
    const el = document.getElementById("sidebarNav");
    if (!el) return;

    const API =
      typeof Offcanvas !== "undefined"
        ? Offcanvas
        : (window as any).bootstrap?.Offcanvas;
    if (!API) return;

    const instance = API.getOrCreateInstance(el);
    if (el.classList.contains("show")) {
      instance.hide();
    }
  };

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };

  const visibleItems = NavItems.filter((item) => canSeeNavItem(roleId, item, hasFeature, isApprover));
  const helpTopic = getHelpTopicForPath(location.pathname);

  useEffect(() => {
    setHelpOpen(false);
  }, [location.pathname]);

  return (
    <div className="d-none d_show">
      <div className="d-flex tp-bg">
        <div className="me-auto">
          <button
            type="button"
            className="bar_btn"
            onClick={openMenu}
            aria-controls="sidebarNav"
          >
            <img src="/images/bar.svg" alt="Open menu" />
          </button>
        </div>

        <div className="ms-auto me-auto">
          <Link to={homeRoute}>
            <img className={styles.mobileHeaderLogo} src="/images/appLogo.svg" alt="Logo" />
          </Link>
        </div>

        <div className={`ms-auto bl-btn ${styles.mobileHeaderActions}`}>
          <button
            type="button"
            className={`${styles["layout-help-button"]} ${styles["layout-help-button--mobile"]} ${isHelpOpen ? styles["layout-help-button--active"] : ""}`}
            onClick={() => setHelpOpen(true)}
            aria-label="Open help"
            aria-haspopup="dialog"
            aria-expanded={isHelpOpen}
          >
            <HelpIcon />
          </button>
        </div>
      </div>

      <div
        className="offcanvas offcanvas-start"
        tabIndex={-1}
        id="sidebarNav"
        aria-label="Main navigation"
      >
        <div
          className={`offcanvas-header p-0 ${styles.offcanvasHeaderCollapsed}`}
        >
          <button
            type="button"
            className={`btn-close text-reset ${styles.offcanvasCloseBtn}`}
            aria-label="Close"
            onClick={closeMenu}
          >
            <img src="/images/close-circle.svg" alt="Close menu" />
          </button>
        </div>

        <div className="offcanvas-body p-0">
          <div className="left-side">
            <div className={styles.sidebarUserSection}>
              <div className={styles.sidebarUserRow}>
                <div className={styles.sidebarAvatarWrapper}>
                  <InitialsAvatar
                    size={42}
                    firstName={user?.firstName}
                    lastName={user?.lastName}
                    fullName={displayName}
                    email={user?.emailAddress || (user as any)?.EmailAddress}
                    title={displayName}
                  />
                </div>

                <div className={styles.sidebarUserInfo}>
                  <div className={styles.sidebarDisplayName}>
                    {displayName}
                  </div>

                  <div className={styles.sidebarTenantRoleRow}>
                    <span
                      className={styles.sidebarTenantName}
                      title={activeTenantName}
                    >
                      {activeTenantName}
                    </span>

                    <span
                      className={styles.sidebarRoleBadge}
                      title={`RoleId: ${roleId || "N/A"}`}
                    >
                      {roleLabel}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.sidebarActionLinks}>
                {canSwitchTenants && (
                  <Link
                    to="/selecttenant"
                    onClick={closeMenu}
                    className={styles.actionLink}
                  >
                    Switch tenants
                  </Link>
                )}

                <Link
                  to="/editprofile"
                  onClick={closeMenu}
                  className={styles.actionLink}
                >
                  Edit profile
                </Link>
              </div>
            </div>

            <div className={styles.sidebarDivider} />

            <ul>
              {visibleItems.map((item) => (
                <li key={item.key}>
                  <Link
                    to={item.to}
                    onClick={closeMenu}
                    className={
                      location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to + "/"))
                        ? "active" : ""
                    }
                  >
                    <img src={item.icon} alt="" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="fixed-div text-center">
              <hr />
              <button
                type="button"
                onClick={handleLogout}
                className={styles.logoutButton}
              >
                <img src="/images/logout.svg" alt="" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
      <HelpDrawer
        isOpen={isHelpOpen}
        onClose={() => setHelpOpen(false)}
        topic={helpTopic}
      />
    </div>
  );
};

// ----------------------------
// Desktop Sidebar Navigation
// ----------------------------
const NavBar = ({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) => {
  const location = useLocation();
  const { user, isApprover } = useUser();
  const { hasFeature } = useTier();
  const roleId = Number(user?.roleId);
  const visibleItems = NavItems.filter((item) => canSeeNavItem(roleId, item, hasFeature, isApprover));
  const homeRoute = getDefaultNavRoute(roleId, hasFeature);

  return (
    <nav className="left-side" id="app-sidebar-nav" aria-label="Main navigation">
      <div className={styles.sidebarToggleRow}>
        <Link to={homeRoute} className={styles.sidebarLogoLink} aria-label="Go to home">
          <img
            className={collapsed ? styles.sidebarLogoCollapsed : styles.sidebarLogoExpanded}
            src="/images/appLogo.svg"
            alt="Logo"
          />
        </Link>
        <button
          type="button"
          className={styles.sidebarCollapseToggle}
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-controls="app-sidebar-nav"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="sidebar-toggle"
        >
          <svg
            className={styles.sidebarCollapseIcon}
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M15 6l-6 6 6 6"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
            />
          </svg>
        </button>
      </div>

      <ul>
        {visibleItems.map((item) => (
          <li key={item.key}>
            <Link
              to={item.to}
              className={
                location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to + "/"))
                  ? "active" : ""
              }
              title={collapsed ? item.label : undefined}
              aria-label={collapsed ? item.label : undefined}
              data-testid={`nav-${item.key}`}
            >
              <img src={item.icon} alt="" />
              {!collapsed && item.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// ----------------------------
// Main Content
// ----------------------------
const MainContent = ({ children }: { children: React.ReactNode }) => {
  return <div>{children}</div>;
};

const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTotal =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;

      if (scrollTotal <= 0) {
        setIsVisible(false);
        return;
      }

      setIsVisible(document.documentElement.scrollTop / scrollTotal > 0.02);
    };

    document.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => document.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      type="button"
      className={`scrollToTopBtn ${isVisible ? "showBtn" : ""}`}
      onClick={scrollToTop}
      aria-label="Scroll to top"
    >
      <i className="fa-solid fa-angles-up"></i>
    </button>
  );
};

// ----------------------------
// Layout.js (PageLayout Component)
// ----------------------------
const PageLayout = ({ children }: { children: React.ReactNode }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem("sidebarCollapsed") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem("sidebarCollapsed", sidebarCollapsed ? "1" : "0");
    } catch {
      // Ignore storage errors; the visual toggle still works for this session.
    }
  }, [sidebarCollapsed]);

  return (
    <>
      <div className="container">
        <MobileNavBar />

        <div className="row g-0">
          <div className={`column-1 ${sidebarCollapsed ? 'column-1--collapsed' : ''}`}>
            <NavBar
              collapsed={sidebarCollapsed}
              onToggle={() => setSidebarCollapsed(c => !c)}
            />
          </div>

          <div className={`column-2 ${sidebarCollapsed ? 'column-2--expanded' : ''}`}>
            <Header />
            <SubscriptionBanner />
            <MainContent>{children}</MainContent>
          </div>
        </div>
      </div>

      <ScrollToTopButton />
    </>
  );
};

export default PageLayout;